/**
 * @fileoverview Type definitions for Avatar Selector component
 *
 * This file contains all TypeScript interfaces, types, and constants
 * used by the AvatarSelector component for avatar customization.
 */

import { getEnumStringKeys } from '@utils/enumHelper'

import {
  AccessoriesType,
  ClothingColor,
  ClothingType,
  EyeType,
  EyebrowType,
  FacialHairType,
  Gender,
  HairColor,
  MouthType,
  SkinColor,
  TopType
} from '../../utils/enumAvatar'

export type { Gender }

/**
 * Avatar style options for the overall appearance
 */
export type AvatarStyle = 'Circle' | 'Transparent'

/**
 * Gender string keys for type-safe gender operations
 */
export type GenderKey = 'Boy' | 'Girl' | 'Other'

/**
 * Complete avatar configuration interface
 * Contains all customizable options for an avatar
 * Note: Currently uses string types for compatibility with existing functions.
 * Could be enhanced to use enum key types (e.g., TopTypeKey) for better type safety.
 */
export interface SelectedOptions {
  /** Hair/head top type (hairstyle, hat, etc.) */
  topType: string
  /** Hair color */
  hairColor: string
  /** Accessories like glasses, earrings, etc. */
  accessoriesType: string
  /** Facial hair type (beard, mustache, etc.) */
  facialHairType: string
  /** Clothing type */
  clotheType: string
  /** Clothing color */
  clotheColor: string
  /** Eye type/shape */
  eyeType: string
  /** Eyebrow type/shape */
  eyebrowType: string
  /** Mouth expression type */
  mouthType: string
  /** Skin color */
  skinColor: string
  /** Avatar overall style - optional, defaults to 'Circle' */
  avatarStyle?: AvatarStyle
  /** Gender - optional, used for gender-specific options */
  gender?: Gender
}

/**
 * Avatar data type - alias for SelectedOptions
 * Used in component props for clarity
 */
export type AvatarData = SelectedOptions

/**
 * Props interface for the AvatarSelector component
 */
export interface AvatarSelectorProps {
  /** Callback function called when an avatar is selected/saved */
  onSelect: (avatar: AvatarData) => void
  /** Current avatar configuration to pre-populate the selector */
  currentAvatar?: AvatarData
  /** Default gender to use when no current avatar is provided */
  defaultGender?: Gender
  /** Whether the component is disabled */
  disabled?: boolean
  /** Custom CSS class name */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
}

/**
 * Gender-specific options interface for type safety
 */
export interface GenderSpecificOptions {
  topType: string[]
  clotheType: string[]
}

/**
 * Default options interface - contains all available options for each category
 */
export interface DefaultOptions {
  topType: string[]
  hairColor: string[]
  accessoriesType: string[]
  facialHairType: string[]
  clotheType: string[]
  clotheColor: string[]
  eyeType: string[]
  eyebrowType: string[]
  mouthType: string[]
  skinColor: string[]
}

/**
 * Validation result interface for option validation
 */
export interface ValidationResult {
  isValid: boolean
  errorMessage?: string
}

/**
 * Avatar preset interface for predefined avatar configurations
 */
export interface AvatarPreset {
  id: string
  name: string
  description?: string
  configuration: SelectedOptions
  tags?: string[]
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Validates if a value exists in an array
 *
 * @param value - Value to validate
 * @param allowedValues - Array of allowed values
 * @returns True if value is valid
 */
export const isValidOption = (
  value: string,
  allowedValues: string[]
): boolean => {
  return allowedValues.includes(value)
}

/**
 * Gets a random element from an array
 *
 * @param array - Array to pick from
 * @returns Random element from the array
 */
export const getRandomArrayElement = <T>(array: T[]): T => {
  if (array.length === 0) {
    throw new Error('Cannot get random element from empty array')
  }
  return array[Math.floor(Math.random() * array.length)]
}

/**
 * Validates a complete avatar configuration
 *
 * @param avatar - Avatar configuration to validate
 * @returns Validation result with details
 */
export const validateAvatarConfiguration = (
  avatar: Partial<SelectedOptions>
): ValidationResult => {
  const requiredFields: (keyof SelectedOptions)[] = [
    'topType',
    'hairColor',
    'accessoriesType',
    'facialHairType',
    'clotheType',
    'clotheColor',
    'eyeType',
    'eyebrowType',
    'mouthType',
    'skinColor'
  ]

  for (const field of requiredFields) {
    if (!avatar[field]) {
      return {
        isValid: false,
        errorMessage: `Missing required field: ${field}`
      }
    }
  }

  return { isValid: true }
}

// =============================================================================
// CONSTANTS AND DATA
// =============================================================================

/**
 * Hair styles specifically appropriate for boys
 */
const BOY_HAIR_STYLES: string[] = [
  'NoHair',
  'Eyepatch',
  'Hat',
  'Turban',
  'WinterHat1',
  'WinterHat2',
  'WinterHat3',
  'WinterHat4',
  'ShortHairDreads01',
  'ShortHairDreads02',
  'ShortHairFrizzle',
  'ShortHairShaggyMullet',
  'ShortHairShortCurly',
  'ShortHairShortFlat',
  'ShortHairShortRound',
  'ShortHairShortWaved',
  'ShortHairSides',
  'ShortHairTheCaesar',
  'ShortHairTheCaesarSidePart'
] as const

/**
 * Hair styles specifically appropriate for girls
 */
const GIRL_HAIR_STYLES: string[] = [
  'NoHair',
  'Eyepatch',
  'Hat',
  'Hijab',
  'WinterHat1',
  'WinterHat2',
  'WinterHat3',
  'WinterHat4',
  'LongHairBigHair',
  'LongHairBob',
  'LongHairBun',
  'LongHairCurly',
  'LongHairCurvy',
  'LongHairDreads',
  'LongHairFrida',
  'LongHairFro',
  'LongHairFroBand',
  'LongHairNotTooLong',
  'LongHairShavedSides',
  'LongHairMiaWallace',
  'LongHairStraight',
  'LongHairStraight2',
  'LongHairStraightStrand'
] as const

/**
 * Gender-specific avatar options with type safety
 * Maps gender keys to their appropriate customization options
 */
export const genderSpecificOptions: Record<GenderKey, GenderSpecificOptions> = {
  Other: {
    topType: getEnumStringKeys(TopType),
    clotheType: getEnumStringKeys(ClothingType)
  },
  Boy: {
    topType: getEnumStringKeys(TopType).filter((type) =>
      BOY_HAIR_STYLES.includes(type)
    ),
    clotheType: getEnumStringKeys(ClothingType)
  },
  Girl: {
    topType: getEnumStringKeys(TopType).filter((type) =>
      GIRL_HAIR_STYLES.includes(type)
    ),
    clotheType: getEnumStringKeys(ClothingType)
  }
} as const

/**
 * Default options for all avatar customization categories
 * Generated from enums to ensure consistency and type safety
 */
export const defaultOptions: DefaultOptions = {
  topType: getEnumStringKeys(TopType),
  hairColor: getEnumStringKeys(HairColor),
  accessoriesType: getEnumStringKeys(AccessoriesType),
  facialHairType: getEnumStringKeys(FacialHairType),
  clotheType: getEnumStringKeys(ClothingType),
  clotheColor: getEnumStringKeys(ClothingColor),
  eyeType: getEnumStringKeys(EyeType),
  eyebrowType: getEnumStringKeys(EyebrowType),
  mouthType: getEnumStringKeys(MouthType),
  skinColor: getEnumStringKeys(SkinColor)
} as const

/**
 * Default avatar configuration values
 */
export const DEFAULT_AVATAR_CONFIG: SelectedOptions = {
  topType: 'ShortHairShortFlat',
  hairColor: 'Brown',
  accessoriesType: 'Blank',
  facialHairType: 'Blank',
  clotheType: 'ShirtCrewNeck',
  clotheColor: 'Blue01',
  eyeType: 'Default',
  eyebrowType: 'Default',
  mouthType: 'Smile',
  skinColor: 'Light',
  avatarStyle: 'Circle',
  gender: Gender.Boy
} as const

/**
 * Component size constants
 */
export const AVATAR_SIZES = {
  SMALL: 80,
  MEDIUM: 120,
  LARGE: 160,
  EXTRA_LARGE: 200
} as const

/**
 * Modal configuration constants
 */
export const MODAL_CONFIG = {
  DEFAULT_WIDTH: 680,
  MIN_WIDTH: 600,
  MAX_WIDTH: 800
} as const

/**
 * Type guard to check if a value is a valid Gender
 *
 * @param value - Value to check
 * @returns True if value is a valid Gender enum value
 */
export const isValidGender = (value: unknown): value is Gender => {
  return Object.values(Gender).includes(value as Gender)
}

/**
 * Type guard to check if a value is a valid AvatarStyle
 *
 * @param value - Value to check
 * @returns True if value is a valid AvatarStyle
 */
export const isValidAvatarStyle = (value: unknown): value is AvatarStyle => {
  return value === 'Circle' || value === 'Transparent'
}

/**
 * Type guard to check if an object is a valid SelectedOptions
 *
 * @param obj - Object to validate
 * @returns True if object matches SelectedOptions interface
 */
export const isValidSelectedOptions = (
  obj: unknown
): obj is SelectedOptions => {
  if (!obj || typeof obj !== 'object' || obj === null) return false

  const typedObj = obj as Record<string, unknown>

  const requiredStringFields = [
    'topType',
    'hairColor',
    'accessoriesType',
    'facialHairType',
    'clotheType',
    'clotheColor',
    'eyeType',
    'eyebrowType',
    'mouthType',
    'skinColor'
  ]

  for (const field of requiredStringFields) {
    if (typeof typedObj[field] !== 'string') return false
  }

  // Optional fields validation
  if (typedObj.avatarStyle && !isValidAvatarStyle(typedObj.avatarStyle))
    return false
  if (typedObj.gender && !isValidGender(typedObj.gender)) return false

  return true
}

/**
 * Type guard to check if a gender key is valid
 *
 * @param key - Key to check
 * @returns True if key is a valid GenderKey
 */
export const isValidGenderKey = (key: unknown): key is GenderKey => {
  return key === 'Boy' || key === 'Girl' || key === 'Other'
}

/**
 * Creates a deep copy of an avatar configuration
 *
 * @param avatar - Avatar configuration to clone
 * @returns Deep copy of the avatar configuration
 */
export const cloneAvatarConfig = (avatar: SelectedOptions): SelectedOptions => {
  return {
    topType: avatar.topType,
    hairColor: avatar.hairColor,
    accessoriesType: avatar.accessoriesType,
    facialHairType: avatar.facialHairType,
    clotheType: avatar.clotheType,
    clotheColor: avatar.clotheColor,
    eyeType: avatar.eyeType,
    eyebrowType: avatar.eyebrowType,
    mouthType: avatar.mouthType,
    skinColor: avatar.skinColor,
    avatarStyle: avatar.avatarStyle,
    gender: avatar.gender
  }
}

/**
 * Merges two avatar configurations, with the second taking precedence
 *
 * @param base - Base avatar configuration
 * @param overrides - Override values
 * @returns Merged avatar configuration
 */
export const mergeAvatarConfigs = (
  base: SelectedOptions,
  overrides: Partial<SelectedOptions>
): SelectedOptions => {
  return {
    ...base,
    ...overrides
  }
}

/**
 * Gets available options for a specific gender and option type
 *
 * @param gender - Gender to get options for
 * @param optionType - Type of option (topType or clotheType)
 * @returns Array of available options
 */
export const getGenderSpecificOptions = (
  gender: Gender,
  optionType: keyof GenderSpecificOptions
): string[] => {
  const genderKey = Gender[gender] as GenderKey
  return genderSpecificOptions[genderKey][optionType] || []
}

// =============================================================================
// PREDEFINED AVATAR PRESETS
// =============================================================================

/**
 * Predefined avatar presets for common configurations
 */
export const AVATAR_PRESETS: Record<string, AvatarPreset> = {
  boyDefault: {
    id: 'boy-default',
    name: 'Default Boy',
    description: 'A standard boy avatar configuration',
    configuration: {
      ...DEFAULT_AVATAR_CONFIG,
      gender: Gender.Boy,
      topType: 'ShortHairShortFlat',
      clotheType: 'ShirtCrewNeck'
    },
    tags: ['boy', 'default', 'casual']
  },
  girlDefault: {
    id: 'girl-default',
    name: 'Default Girl',
    description: 'A standard girl avatar configuration',
    configuration: {
      ...DEFAULT_AVATAR_CONFIG,
      gender: Gender.Girl,
      topType: 'LongHairBob',
      clotheType: 'Overall'
    },
    tags: ['girl', 'default', 'casual']
  },
  professionalBoy: {
    id: 'professional-boy',
    name: 'Professional Boy',
    description: 'A professional-looking boy avatar',
    configuration: {
      ...DEFAULT_AVATAR_CONFIG,
      gender: Gender.Boy,
      topType: 'ShortHairTheCaesar',
      clotheType: 'BlazerShirt',
      clotheColor: 'Black'
    },
    tags: ['boy', 'professional', 'formal']
  },
  professionalGirl: {
    id: 'professional-girl',
    name: 'Professional Girl',
    description: 'A professional-looking girl avatar',
    configuration: {
      ...DEFAULT_AVATAR_CONFIG,
      gender: Gender.Girl,
      topType: 'LongHairStraight',
      clotheType: 'BlazerShirt',
      clotheColor: 'Black'
    },
    tags: ['girl', 'professional', 'formal']
  }
} as const
